import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import CheckIcon from '@/assets/icons/check.svg?react'
import EarthIcon from '@/assets/icons/earth.svg?react'
import { Trans } from '@/components'
import { supportedLanguages } from '@/i18n'
import { cn } from '@/utils/style'

import { UserDropdownItem } from './UserDropdownItem'

interface LanguageSwitcherProps {
  onLanguageChange: () => void
}

export function LanguageSwitcher({ onLanguageChange }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const languages = supportedLanguages.map((lang) => ({
    value: lang.code,
    label: lang.name,
  }))

  const handleLanguageChange = (lang: { value: string; label: string }) => {
    i18n.changeLanguage(lang.value)
    onLanguageChange()
  }

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setShowLanguageMenu(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowLanguageMenu(false)
    }, 100)
  }

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <UserDropdownItem
        icon={EarthIcon}
        label={<Trans i18nKey="header.userDropdown.language">Language</Trans>}
        active={showLanguageMenu}
        trailing={<ArrowRightIcon className="h-4 w-4 shrink-0 text-tertiary" />}
      />

      <div
        className={cn(
          'absolute p-2 top-0 -left-4 -translate-x-full w-[200px] bg-panel-white rounded-2xl py-2 shadow-card-100',
          showLanguageMenu ? 'block' : 'hidden',
        )}
      >
        {languages.map((lang) => (
          <div
            key={lang.value}
            className={`px-4 py-2 cursor-pointer flex items-center justify-between hover:bg-panel-gray-100 rounded-lg ${
              i18n.language === lang.value ? 'text-primary' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation()
              handleLanguageChange(lang)
            }}
          >
            <span className="t-sm">{lang.label}</span>
            {i18n.language === lang.value && <CheckIcon className="w-4 h-4 text-tertiary" />}
          </div>
        ))}
      </div>
    </div>
  )
}
